/**
 * @file App.tsx
 * @description Main Kitchen Display System (KDS) & Chef Operations Console for TableGreet.
 * 
 * ARCHITECTURAL RESPONSIBILITIES:
 * 1. Coordinates station filtering (Hot, Grill, Cold, Dessert, Drinks).
 * 2. Renders responsive ticket grid with optimistic status transitions.
 * 3. Dispatches audio chimes on new ticket arrivals.
 * 4. Mounts All-Day production drawer and Chef Performance statistics.
 */

import React, { useState, useMemo } from 'react';
import { ChefTopBar } from './shared/components/ChefTopBar';
import { OrderCard } from './features/orders/components/OrderCard';
import { AllDayDrawer } from './features/allday/components/AllDayDrawer';
import { ChefStatsModal } from './features/stats/components/ChefStatsModal';
import { ChefLoginModal } from './features/auth/components/ChefLoginModal';
import { ChefNewOrderModal } from './features/orders/components/ChefNewOrderModal';
import { KitchenOpsDrawer } from './features/kitchenops/components/KitchenOpsDrawer';
import { useChefAuth } from './features/auth/hooks/useChefAuth';
import { useKitchenOrders } from './features/orders/hooks/useKitchenOrders';
import { useKitchenOps } from './features/kitchenops/hooks/useKitchenOps';
import { calculateAllDayCounts } from './features/allday/services/allDayCalculator.service';
import { StationFilterTabs } from './features/stations/components/StationFilterTabs';
import type { KitchenOrderStatus, KitchenPriority, KitchenStation } from './types/order.types';
import { ChefHat, Flame, CheckCircle2, Search, Plus, X, RefreshCw } from 'lucide-react';
import logo from './assets/logo.svg';

export const App: React.FC = () => {
  const { sessionUser, activeVenue, login, logout, isLoading: isAuthLoading } = useChefAuth();
  const { orders, isLoading: isOrdersLoading, error, changeStatus, togglePriority, refreshOrders } = useKitchenOrders(activeVenue?.id || null);
  const kitchenOps = useKitchenOps(activeVenue?.id || null);

  const [orderFilter, setOrderFilter] = useState<'all' | 'new' | 'preparing' | 'ready' | 'done'>('all');
  const [selectedStation, setSelectedStation] = useState<KitchenStation>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isAllDayOpen, setIsAllDayOpen] = useState<boolean>(false);
  const [isStatsOpen, setIsStatsOpen] = useState<boolean>(false);
  const [isKitchenOpsOpen, setIsKitchenOpsOpen] = useState<boolean>(false);
  const [isNewOrderOpen, setIsNewOrderOpen] = useState<boolean>(false);

  // Operational metrics for orders: Kitchen tracks active orders that contain culinary items (drinks-only tickets route to waiters)
  const activeKitchenOrders = useMemo(
    () => orders.filter(o => {
      if (o.status === 'done' || o.status === 'cancelled') return false;
      return o.items.some(i => i.status !== 'cancelled' && (i.kitchen_station || 'hot').toLowerCase() !== 'drinks');
    }),
    [orders]
  );
  const activeCount = activeKitchenOrders.length;
  const newCount = useMemo(() => activeKitchenOrders.filter(o => o.status === 'new').length, [activeKitchenOrders]);
  const preparingCount = useMemo(() => activeKitchenOrders.filter(o => o.status === 'preparing').length, [activeKitchenOrders]);
  const readyCount = useMemo(() => activeKitchenOrders.filter(o => o.status === 'ready').length, [activeKitchenOrders]);
  const doneCount = useMemo(() => orders.filter(o => o.status === 'done').length, [orders]);

  // Aggregate active orders count per kitchen station for station tab badges
  const stationCounts = useMemo(() => {
    const counts: Record<string, number> = { all: activeCount };
    for (const order of orders) {
      if (order.status === 'done' || order.status === 'cancelled') continue;
      for (const item of order.items) {
        if (item.status === 'cancelled') continue;
        const st = (item.kitchen_station || 'hot').toLowerCase();
        counts[st] = (counts[st] || 0) + 1;
      }
    }
    return counts;
  }, [orders, activeCount]);

  // Real-time glanceable all-day batch production summary across active orders filtered by station
  const liveAllDayItems = useMemo(() => {
    return calculateAllDayCounts(activeKitchenOrders, selectedStation);
  }, [activeKitchenOrders, selectedStation]);

  // Filter orders by pipeline status and search query, prioritizing RUSH orders and oldest tickets first (FIFO)
  const filteredOrders = useMemo(() => {
    const list = orders.filter(order => {
      // 1. Status filter: 'all' shows active kitchen tickets (new, preparing, ready); 'done' shows completed/served
      if (orderFilter === 'all') {
        if (order.status === 'done' || order.status === 'cancelled') {
          return false;
        }
      } else if (order.status !== orderFilter) {
        return false;
      }

      // 2. Station filter: isolates tickets containing items for the selected station
      if (selectedStation === 'all') {
        // When viewing all kitchen stations, tickets with only drinks are handled exclusively by waitstaff and the bar
        const hasCulinaryItem = order.items.some(
          i => i.status !== 'cancelled' && (i.kitchen_station || 'hot').toLowerCase() !== 'drinks'
        );
        if (!hasCulinaryItem) return false;
      } else {
        const hasStationItem = order.items.some(
          i => i.status !== 'cancelled' && (i.kitchen_station || 'hot').toLowerCase() === selectedStation.toLowerCase()
        );
        if (!hasStationItem) return false;
      }

      // 3. Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesTable = order.table_number.toLowerCase().includes(q);
        const matchesOrder = order.order_number.toLowerCase().includes(q);
        const matchesWaiter = order.waiter_name?.toLowerCase().includes(q) || false;
        const matchesNotes = order.customer_notes?.toLowerCase().includes(q) || false;
        const matchesItems = order.items.some(i => i.name.toLowerCase().includes(q));
        if (!matchesTable && !matchesOrder && !matchesWaiter && !matchesNotes && !matchesItems) {
          return false;
        }
      }

      return true;
    });

    // Kitchen sorting rule: Rush orders strictly first, then FIFO (oldest orders first)
    return list.sort((a, b) => {
      if (a.priority === 'rush' && b.priority !== 'rush') return -1;
      if (b.priority === 'rush' && a.priority !== 'rush') return 1;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  }, [orders, orderFilter, searchQuery]);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center text-zinc-400">
        <img src={logo} className="w-12 h-12 object-contain animate-pulse mb-3" alt="TableGreet" />
        <p className="font-mono text-xs text-zinc-400 tracking-wider">INITIALIZING TABLEGREET KITCHEN CONSOLE...</p>
      </div>
    );
  }

  if (!sessionUser) {
    return <ChefLoginModal onLogin={login} />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col overflow-hidden">
      {/* Top Header */}
      <ChefTopBar
        venueName={activeVenue?.name || 'TableGreet Kitchen'}
        onOpenAllDay={() => setIsAllDayOpen(true)}
        onOpenStats={() => setIsStatsOpen(true)}
        onOpenKitchenOps={() => setIsKitchenOpsOpen(true)}
        onLogout={logout}
        activeOrderCount={activeCount}
        kitchenLoadStatus={kitchenOps.kitchenLoadStatus}
        hasWhiteboardNote={Boolean(kitchenOps.managerNote?.trim() || kitchenOps.chefNote?.trim())}
      />

      {/* Main KDS Board */}
      <main className="flex-1 p-4 overflow-y-auto space-y-4">
        {/* Filter Toolbar: All, New, Preparing, Ready, Done + Search + New Order */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 bg-[#121212] p-3 rounded-2xl border border-white/10">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 xl:pb-0 scrollbar-none flex-nowrap">
            {/* All */}
            <button
              type="button"
              onClick={() => setOrderFilter('all')}
              className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                orderFilter === 'all'
                  ? 'bg-white/15 text-white border border-white/20 font-black'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <span>all</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
                {activeCount}
              </span>
            </button>

            {/* New */}
            <button
              type="button"
              onClick={() => setOrderFilter('new')}
              className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                orderFilter === 'new'
                  ? 'bg-[#ff4f00] text-white shadow-lg shadow-[#ff4f00]/30 font-black'
                  : newCount > 0
                  ? 'text-[#ff4f00] hover:text-orange-400'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <ChefHat className="w-3.5 h-3.5" />
              <span>new</span>
              {newCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-black/40 text-white animate-pulse">
                  {newCount}
                </span>
              )}
            </button>

            {/* Preparing */}
            <button
              type="button"
              onClick={() => setOrderFilter('preparing')}
              className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                orderFilter === 'preparing'
                  ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/30 font-black'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Flame className="w-3.5 h-3.5" />
              <span>preparing</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
                {preparingCount}
              </span>
            </button>

            {/* Ready */}
            <button
              type="button"
              onClick={() => setOrderFilter('ready')}
              className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                orderFilter === 'ready'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30 font-black'
                  : readyCount > 0
                  ? 'text-purple-400 hover:text-purple-300'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>ready</span>
              {readyCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-purple-950 text-purple-200 animate-pulse">
                  {readyCount}
                </span>
              )}
            </button>

            {/* Done */}
            <button
              type="button"
              onClick={() => setOrderFilter('done')}
              className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 shrink-0 active:scale-[0.98] ${
                orderFilter === 'done'
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/30 font-black'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>done</span>
              <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-white/10 text-zinc-300">
                {doneCount}
              </span>
            </button>
          </div>

          {/* Search & New Order Action */}
          <div className="flex items-center gap-2 w-full xl:w-auto shrink-0">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search table, order..."
                className="w-full min-h-[40px] pl-9 pr-8 py-2 rounded-xl bg-[#0a0a0a] border border-white/10 text-base sm:text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#ff4f00] transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="min-w-[32px] min-h-[32px] flex items-center justify-center absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white cursor-pointer"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsNewOrderOpen(true)}
              className="min-h-[40px] px-3.5 py-2 rounded-xl bg-[#ff4f00] hover:bg-[#e64700] active:scale-[0.98] text-white text-xs font-black uppercase flex items-center gap-1.5 shadow-lg shadow-[#ff4f00]/25 transition-all cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span>New Order</span>
            </button>
          </div>
        </div>

        {/* Station Routing Tabs: Hot, Grill, Cold, Dessert, Drinks */}
        <div className="bg-[#121212] p-2.5 rounded-2xl border border-white/5 flex items-center justify-between gap-3 overflow-x-auto scrollbar-none">
          <StationFilterTabs
            currentStation={selectedStation}
            onSelectStation={setSelectedStation}
            stationCounts={stationCounts}
          />
        </div>

        {/* Live All-Day Production Bar: Shows batch totals across open orders at a single glance */}
        {liveAllDayItems.length > 0 && orderFilter !== 'done' && (
          <div className="flex items-center gap-2 p-2 rounded-2xl bg-zinc-950/90 border border-amber-500/30 overflow-x-auto scrollbar-none shadow-lg shadow-black/40">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 shrink-0">
              <Flame className="w-3.5 h-3.5 animate-pulse" />
              <span className="text-[11px] font-black tracking-wider uppercase">ALL-DAY BATCH</span>
            </div>
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none flex-nowrap py-0.5">
              {liveAllDayItems.slice(0, 15).map(item => (
                <span
                  key={item.itemId}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-zinc-900 border border-zinc-800 text-xs text-zinc-200 shrink-0 font-medium"
                >
                  <span className="font-mono font-black text-amber-400 bg-amber-500/10 px-1.5 py-0.2 rounded border border-amber-500/20">
                    {item.totalQuantity}×
                  </span>
                  <span className="truncate max-w-[160px]">{item.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-xl bg-rose-950/60 border border-rose-800/80 text-rose-300 text-xs font-semibold flex items-center justify-between">
            <span>{error}</span>
            <button
              type="button"
              onClick={refreshOrders}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-rose-900/50 hover:bg-rose-900 text-rose-200 text-xs cursor-pointer"
            >
              <RefreshCw className="w-3 h-3" />
              <span>Retry</span>
            </button>
          </div>
        )}

        {isOrdersLoading && orders.length === 0 ? (
          <div className="h-96 flex flex-col items-center justify-center text-zinc-500">
            <RefreshCw className="w-8 h-8 animate-spin mb-2 text-[#ff4f00]" />
            <p className="text-sm font-medium">Synchronizing live tickets...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="h-96 flex flex-col items-center justify-center text-zinc-500 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#121212] flex items-center justify-center mb-3 border border-white/10">
              <ChefHat className="w-8 h-8 text-zinc-600" />
            </div>
            <h3 className="text-base font-bold text-zinc-200">
              {searchQuery ? 'No Matching Tickets' : `No Orders in "${orderFilter}"`}
            </h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-xs">
              {searchQuery
                ? 'No kitchen orders match your search query.'
                : orderFilter === 'all'
                ? 'No orders waiting in the kitchen right now.'
                : `There are currently no orders in the ${orderFilter} stage.`}
            </p>
            {(searchQuery || orderFilter !== 'all') && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  setOrderFilter('all');
                }}
                className="mt-3 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold text-white transition-colors cursor-pointer"
              >
                Reset Filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3.5">
            {filteredOrders.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                onStatusChange={(id: string, st: KitchenOrderStatus) => changeStatus(id, st)}
                onPriorityToggle={(id: string, p: KitchenPriority) => togglePriority(id, p)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Direct Order Creation Modal */}
      <ChefNewOrderModal
        isOpen={isNewOrderOpen}
        onClose={() => setIsNewOrderOpen(false)}
        venueId={activeVenue?.id || ''}
        onOrderCreated={refreshOrders}
      />

      {/* All Day Drawer */}
      <AllDayDrawer
        isOpen={isAllDayOpen}
        onClose={() => setIsAllDayOpen(false)}
        orders={activeKitchenOrders}
        selectedStation={selectedStation}
      />

      {/* Production KPIs Modal */}
      <ChefStatsModal
        isOpen={isStatsOpen}
        onClose={() => setIsStatsOpen(false)}
        venueId={activeVenue?.id || null}
      />

      {/* Kitchen Station Inventory & 86 Drawer */}
      <KitchenOpsDrawer
        isOpen={isKitchenOpsOpen}
        onClose={() => setIsKitchenOpsOpen(false)}
        ops={kitchenOps}
      />
    </div>
  );
};

export default App;
